import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  Clock,
  Target,
  Calendar,
  Download,
  Filter,
  RefreshCw,
  PieChart,
  Activity,
  CheckCircle
} from 'lucide-react';
import { useAuthStore, api } from '../auth/authStore';
import { UserDashboardLayout } from './UserDashboardLayout';
import toast from 'react-hot-toast';

interface ReportData {
  teamPerformance: {
    totalTasks: number;
    completedTasks: number;
    completionRate: number;
    avgCompletionTime: number;
    totalHours: number;
    billableHours: number;
    utilizationRate: number;
  };
  tasksByStatus: {
    todo: number;
    inProgress: number;
    review: number;
    completed: number;
  };
  tasksByPriority: {
    low: number;
    medium: number;
    high: number;
    urgent: number;
  };
  memberPerformance: Array<{
    id: string;
    name: string;
    tasksCompleted: number;
    hoursWorked: number;
    utilizationRate: number;
    completionRate: number;
  }>;
  timeTracking: {
    dailyHours: Array<{ date: string; hours: number }>;
    weeklyHours: Array<{ week: string; hours: number }>;
    monthlyHours: Array<{ month: string; hours: number }>;
  };
}

interface MetricCard {
  title: string;
  value: string | number;
  change?: string;
  trend?: 'up' | 'down' | 'neutral';
  icon: React.ElementType;
  color: string;
}

const MetricCard: React.FC<MetricCard> = ({ title, value, change, trend, icon: Icon, color }) => (
  <div className="bg-white rounded-lg p-6 border border-gray-200">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {change && (
          <p className={`text-sm flex items-center mt-1 ${
            trend === 'up' ? 'text-green-600' : 
            trend === 'down' ? 'text-red-600' : 
            'text-gray-600'
          }`}>
            {trend === 'up' && <TrendingUp className="w-4 h-4 mr-1" />}
            {trend === 'down' && <TrendingDown className="w-4 h-4 mr-1" />}
            {change}
          </p>
        )}
      </div>
      <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

const ChartPlaceholder: React.FC<{ title: string; height?: string }> = ({ title, height = "h-64" }) => (
  <div className={`bg-gray-50 rounded-lg ${height} flex items-center justify-center border-2 border-dashed border-gray-300`}>
    <div className="text-center">
      <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-2" />
      <p className="text-gray-600 font-medium">{title}</p>
      <p className="text-sm text-gray-500">Chart visualization coming soon</p>
    </div>
  </div>
);

export const UserReports: React.FC = () => {
  const { user } = useAuthStore();
  const [reportData, setReportData] = useState<ReportData>({
    teamPerformance: {
      totalTasks: 0,
      completedTasks: 0,
      completionRate: 0,
      avgCompletionTime: 0,
      totalHours: 0,
      billableHours: 0,
      utilizationRate: 0,
    },
    tasksByStatus: {
      todo: 0,
      inProgress: 0,
      review: 0,
      completed: 0,
    },
    tasksByPriority: {
      low: 0,
      medium: 0,
      high: 0,
      urgent: 0,
    },
    memberPerformance: [],
    timeTracking: {
      dailyHours: [],
      weeklyHours: [],
      monthlyHours: [],
    },
  });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30'); // days
  const [reportType, setReportType] = useState('overview');

  useEffect(() => {
    fetchReportData();
  }, [dateRange]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/reports/dashboard?days=${dateRange}`);
      if (response.data.success) {
        setReportData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error('Failed to load report data');
      // Set mock data for demonstration
      setReportData({
        teamPerformance: {
          totalTasks: 156,
          completedTasks: 142,
          completionRate: 91,
          avgCompletionTime: 4.2,
          totalHours: 320,
          billableHours: 288,
          utilizationRate: 85,
        },
        tasksByStatus: {
          todo: 24,
          inProgress: 18,
          review: 12,
          completed: 142,
        },
        tasksByPriority: {
          low: 45,
          medium: 78,
          high: 28,
          urgent: 5,
        },
        memberPerformance: [
          { id: '1', name: 'John Smith', tasksCompleted: 28, hoursWorked: 85, utilizationRate: 88, completionRate: 95 },
          { id: '2', name: 'Sarah Johnson', tasksCompleted: 32, hoursWorked: 92, utilizationRate: 91, completionRate: 89 },
          { id: '3', name: 'Mike Davis', tasksCompleted: 25, hoursWorked: 78, utilizationRate: 82, completionRate: 92 },
          { id: '4', name: 'Lisa Wilson', tasksCompleted: 30, hoursWorked: 88, utilizationRate: 87, completionRate: 94 },
        ],
        timeTracking: {
          dailyHours: [],
          weeklyHours: [],
          monthlyHours: [],
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const exportReport = () => {
    toast('Export functionality coming soon', { icon: 'ℹ️' });
  };

  if (loading) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Team Reports</h2>
              <p className="text-gray-600">
                Analyze team performance and productivity metrics.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="365">Last year</option>
              </select>
              <button
                onClick={fetchReportData}
                className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </button>
              <button
                onClick={exportReport}
                className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Total Tasks"
            value={reportData.teamPerformance.totalTasks}
            change={`${reportData.teamPerformance.completedTasks} completed`}
            trend="neutral"
            icon={Target}
            color="bg-blue-500"
          />
          <MetricCard
            title="Completion Rate"
            value={`${reportData.teamPerformance.completionRate}%`}
            change="+5% from last period"
            trend="up"
            icon={CheckCircle}
            color="bg-green-500"
          />
          <MetricCard
            title="Total Hours"
            value={`${reportData.teamPerformance.totalHours}h`}
            change={`${reportData.teamPerformance.billableHours}h billable`}
            trend="neutral"
            icon={Clock}
            color="bg-purple-500"
          />
          <MetricCard
            title="Team Utilization"
            value={`${reportData.teamPerformance.utilizationRate}%`}
            change="+3% from last period"
            trend="up"
            icon={Activity}
            color="bg-orange-500"
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Tasks by Status */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Tasks by Status</h3>
              <PieChart className="w-5 h-5 text-gray-400" />
            </div>
            <ChartPlaceholder title="Tasks Distribution Chart" />
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{reportData.tasksByStatus.todo}</p>
                <p className="text-sm text-gray-600">To Do</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-600">{reportData.tasksByStatus.inProgress}</p>
                <p className="text-sm text-gray-600">In Progress</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">{reportData.tasksByStatus.review}</p>
                <p className="text-sm text-gray-600">Review</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{reportData.tasksByStatus.completed}</p>
                <p className="text-sm text-gray-600">Completed</p>
              </div>
            </div>
          </div>

          {/* Tasks by Priority */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Tasks by Priority</h3>
              <BarChart3 className="w-5 h-5 text-gray-400" />
            </div>
            <ChartPlaceholder title="Priority Distribution Chart" />
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">{reportData.tasksByPriority.urgent}</p>
                <p className="text-sm text-gray-600">Urgent</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-600">{reportData.tasksByPriority.high}</p>
                <p className="text-sm text-gray-600">High</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-600">{reportData.tasksByPriority.medium}</p>
                <p className="text-sm text-gray-600">Medium</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{reportData.tasksByPriority.low}</p>
                <p className="text-sm text-gray-600">Low</p>
              </div>
            </div>
          </div>
        </div>

        {/* Team Performance Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Team Member Performance</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Team Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tasks Completed
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hours Worked
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilization Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Completion Rate
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.memberPerformance.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-primary-600 font-medium text-sm">
                            {member.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div className="text-sm font-medium text-gray-900">{member.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {member.tasksCompleted}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {member.hoursWorked}h
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${
                        member.utilizationRate >= 85 ? 'text-green-600' :
                        member.utilizationRate >= 70 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {member.utilizationRate}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${
                        member.completionRate >= 90 ? 'text-green-600' :
                        member.completionRate >= 80 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {member.completionRate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Time Tracking Chart */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Time Tracking Trends</h3>
            <TrendingUp className="w-5 h-5 text-gray-400" />
          </div>
          <ChartPlaceholder title="Time Tracking Line Chart" height="h-80" />
        </div>
      </div>
    </UserDashboardLayout>
  );
};
